import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, CardHeader, CardContent, Box, Typography, Button, TextField, MenuItem,
  Alert, CircularProgress, Divider, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { AdminService } from '../../services/adminService';

/**
 * Abonnement offert — Aventurier ou Champion, pour une durée à partir d'aujourd'hui.
 *
 * La date de fin est REMPLACÉE (pas prolongée) : offrir 30 jours à un compte qui
 * en avait encore 60 d'offerts ramène la fin à J+30. Un abonnement payant en
 * cours est refusé par le serveur : il se modifie chez Stripe ou la boutique.
 */

const PLANS = [
  { value: 'premium', label: 'Aventurier' },
  { value: 'premium_plus', label: 'Champion' },
];

const DUREES = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '1 mois (30 jours)' },
  { value: 90, label: '3 mois (90 jours)' },
  { value: 180, label: '6 mois (180 jours)' },
  { value: 365, label: '1 an (365 jours)' },
];

/** Sans valeur par défaut : on veut savoir, plus tard, pourquoi chaque accès a été offert. */
const REASONS = [
  { value: 'geste_commercial', label: 'Geste commercial' },
  { value: 'support_incident', label: 'Support — incident technique' },
  { value: 'beta_testeur', label: 'Bêta-testeur' },
  { value: 'partenariat', label: 'Partenariat / presse / créateur' },
  { value: 'enseignant', label: 'Enseignant / établissement' },
  { value: 'compte_interne', label: 'Compte interne / test' },
  { value: 'autre', label: 'Autre (préciser en note)' },
];

const planLabel = (plan) => (!plan || plan === 'free' ? 'Gratuit' : PLANS.find((p) => p.value === plan)?.label ?? plan);
const reasonLabel = (reason) => REASONS.find((r) => r.value === reason)?.label ?? reason;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

export default function SubscriptionGrantCard({ userId, onGranted }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [planType, setPlanType] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchGrants = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await AdminService.getUserSubscriptionGrants(userId);
      setGrants(res?.grants ?? []);
    } catch (err) {
      console.error('[SubscriptionGrant] chargement:', err);
      setError(err?.response?.data?.error || "Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);

  const jours = Number(durationDays);
  const dureeValide = Number.isInteger(jours) && jours >= 1 && jours <= 730;
  const noteRequise = reason === 'autre' && !note.trim();
  const canGrant = !!planType && dureeValide && !!reason && !noteRequise && !granting;
  const finPrevue = dureeValide ? new Date(Date.now() + jours * 24 * 60 * 60 * 1000) : null;

  const handleGrant = async () => {
    if (!canGrant) return;
    setGranting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await AdminService.grantUserSubscription(userId, {
        planType,
        durationDays: jours,
        reason,
        note: note.trim() || undefined,
      });
      setSuccess(`${planLabel(planType)} offert jusqu'au ${formatDate(res?.endsAt)}.`);
      setNote('');
      await fetchGrants();
      onGranted?.();
    } catch (err) {
      console.error('[SubscriptionGrant] attribution:', err);
      setError(err?.response?.data?.error || "L'attribution a échoué");
    } finally {
      setGranting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Offrir un abonnement"
        subheader="Aventurier ou Champion, à partir d'aujourd'hui — sans paiement"
      />
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          La date de fin est remplacée, pas prolongée. Un abonnement payant en cours
          (Stripe, App Store, Google Play) ne se modifie pas ici.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select size="small" label="Offre" value={planType}
            onChange={(e) => setPlanType(e.target.value)} fullWidth>
            {PLANS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField select size="small" label="Durée" value={DUREES.some((d) => d.value === jours) ? jours : ''}
              onChange={(e) => setDurationDays(String(e.target.value))} sx={{ flex: 1 }}>
              {DUREES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="ou en jours" value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              inputProps={{ min: 1, max: 730, step: 1 }}
              error={durationDays !== '' && !dureeValide}
              helperText={durationDays !== '' && !dureeValide ? '1 à 730' : ' '}
              sx={{ width: 130 }} />
          </Box>

          <TextField select size="small" label="Motif (obligatoire)" value={reason}
            onChange={(e) => setReason(e.target.value)} fullWidth>
            {REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>

          <TextField size="small" label={reason === 'autre' ? 'Note (obligatoire)' : 'Note (facultatif)'}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Ticket support, contexte…" fullWidth />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" onClick={handleGrant} disabled={!canGrant}>
              {granting ? 'Attribution…' : 'Offrir'}
            </Button>
            {planType && finPrevue && (
              <Typography variant="body2" color="text.secondary">
                {planLabel(planType)} jusqu&apos;au {formatDate(finPrevue)}
              </Typography>
            )}
          </Box>
        </Box>

        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Déjà offerts</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}><CircularProgress size={20} /></Box>
        ) : grants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Le</TableCell>
                <TableCell>Offre</TableCell>
                <TableCell>Jusqu&apos;au</TableCell>
                <TableCell>Motif</TableCell>
                <TableCell>Avant</TableCell>
                <TableCell>Par</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grants.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{formatDate(g.createdAt)}</TableCell>
                  <TableCell>{planLabel(g.planType)} ({g.durationDays} j)</TableCell>
                  <TableCell>{formatDate(g.endsAt)}</TableCell>
                  <TableCell>
                    {reasonLabel(g.reason)}
                    {g.note && <Typography variant="caption" color="text.secondary" display="block">{g.note}</Typography>}
                  </TableCell>
                  <TableCell>
                    {planLabel(g.previousPlanType)}
                    {g.previousEndsAt && ` → ${formatDate(g.previousEndsAt)}`}
                  </TableCell>
                  <TableCell>{g.createdByName || g.createdByEmail || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
